import type { PlanTier } from "./planCatalog";

/** Customer-facing labels for the two session audio modes (not CCTV/NVR audio). */
export const SESSION_AUDIO_MODES = {
  system: {
    title: "System audio streaming",
    short: "System audio",
    description:
      "Captures what plays through the PC speakers: Zoom and Teams meetings, softphone calls, browser media, and other application output. Muxed into the same session segment as the screen recording.",
  },
  mic: {
    title: "Mic audio streaming",
    short: "Microphone audio",
    description:
      "Captures the endpoint default microphone alongside the screen stream. Available on Enterprise when your organization enables microphone profiles and completes consent workflows.",
  },
} as const;

export const SESSION_AUDIO_PLAN_MATRIX: Record<
  PlanTier,
  { label: string; detail: string }
> = {
  essential: {
    label: "Screen only",
    detail: "Visual session capture without optional session audio.",
  },
  pro: {
    label: "System audio streaming",
    detail: "System loopback (PC output) muxed into session segments when enabled by policy.",
  },
  enterprise: {
    label: "System + mic audio streaming",
    detail:
      "System loopback plus microphone capture. Stereo split (system left, mic right) or mixed mono profiles for forensic review.",
  },
};

export const SESSION_AUDIO_CALLOUT =
  "Session Audio is an optional add-on (not bundled in base seat plans): Session Audio Pro covers system speaker output; Session Audio Enterprise adds microphone profiles. Essential is screen-only without the add-on.";

export const SESSION_AUDIO_HUB_BLURB =
  "Session Audio add-on modules mux audio into screen recordings when enabled. Session Audio Pro covers speaker output; Session Audio Enterprise adds microphone profiles. Off by default. Never captures keystrokes.";

export const SESSION_AUDIO_FAQ_ITEMS = [
  {
    q: "Does DeskReplay capture audio?",
    a: "DeskReplay captures optional session audio when your organization purchases Session Audio add-on seats, enables audio in Settings, and assigns an audio profile to a capture policy. Essential is screen-only without the add-on. Session Audio Pro muxes system audio streaming (PC speaker output such as Zoom, Teams, softphone, or browser media) into session segments. Session Audio Enterprise adds mic audio streaming (default microphone) alongside system audio, with stereo split or mixed mono profiles. Session audio is not enabled by default and is not the same as keylogging or ambient microphone surveillance.",
  },
  {
    q: "What is the difference between system audio and mic audio?",
    a: "System audio streaming records what the workstation plays through its speakers via loopback capture: meeting audio, call audio, and other PC output. Mic audio streaming records what the operator says into the default microphone. Session Audio Enterprise lets you use both in one segment (stereo split keeps system on the left channel and mic on the right, or mixed mono). Session Audio Pro covers system audio only; microphone capture requires Session Audio Enterprise.",
  },
  {
    q: "Which plans include session audio?",
    a: "Base seat plans (Essential, Pro, Enterprise) do not bundle session audio. Purchase Session Audio Pro or Session Audio Enterprise add-on seats on top of your endpoint plan, then enable audio in Settings and bind profiles to capture policies. Essential remains screen-only unless you add Session Audio modules on Pro+ endpoint pools. See pricing at /pricing#session-audio-addon.",
  },
  {
    q: "Is session audio the same as a keylogger or always-on microphone?",
    a: "No. DeskReplay never captures keystrokes, clipboard contents, mouse coordinates, or biometrics. Session audio activates only when a policy with an audio profile records an in-scope session, and organizations must explicitly enable audio capture in Settings before profiles take effect. Work with HR and counsel on notice and consent before enabling microphone capture.",
  },
] as const;

export const SESSION_AUDIO_GUIDE_SECTIONS = [
  {
    heading: "Why session audio matters",
    blocks: [
      {
        type: "p" as const,
        text: "Screen replay answers what appeared on glass. Session audio answers what the operator and callers heard during the same interval: softphone coaching, meeting instructions, hold music cues, and dispute narratives that never render as on-screen text. DeskReplay muxes optional AAC audio into the primary session segment so investigators replay one synchronized timeline instead of correlating a separate call recorder.",
      },
    ],
  },
  {
    heading: "Two modes: system output and microphone",
    blocks: [
      {
        type: "table" as const,
        headers: ["Mode", "What it captures", "Typical use", "Plan"],
        rows: [
          [
            "System audio streaming",
            "PC speaker output (loopback)",
            "Zoom/Teams meetings, softphone calls, training video audio",
            "Pro and Enterprise",
          ],
          [
            "Mic audio streaming",
            "Default endpoint microphone",
            "Agent voice on calls, verbal confirmations, dispute statements",
            "Enterprise",
          ],
        ],
      },
      {
        type: "callout" as const,
        label: "Forensic layout",
        text: "Enterprise profiles can keep system audio on the left channel and microphone on the right (stereo split), or downmix both into a single mono track. Choose the layout your legal and QA teams prefer before rollout.",
      },
    ],
  },
  {
    heading: "Governance and consent",
    blocks: [
      {
        type: "bullets" as const,
        items: [
          "Organization master switch: audio capture stays off until an authorized admin enables it in Settings.",
          "Policy binding: audio profiles attach to capture policies, so audio records only during in-scope sessions, not 24/7 ambient surveillance.",
          "Plan enforcement: Essential remains screen-only; Pro unlocks system audio; Enterprise unlocks microphone profiles.",
          "Consent workflow: enable microphone capture only after HR and counsel approve notice, purpose limitation, and retention aligned to local law.",
          "Still non-invasive for input: keystrokes, clipboard, and biometrics are never captured regardless of audio settings.",
        ],
      },
    ],
  },
  {
    heading: "Deployment checklist",
    blocks: [
      {
        type: "numbered" as const,
        items: [
          "Confirm plan tier (Pro for system audio, Enterprise for mic plus system).",
          "Enable session audio in organization Settings after security and privacy review.",
          "Create audio profiles (system only, mic only, or both with stereo split or mixed mono).",
          "Attach profiles to proportionate capture policies for the workflows that need audio evidence.",
          "Pilot on a small endpoint group; verify playback sync and export packaging before fleet rollout.",
          "Document who may access audio-bearing segments under your RBAC model.",
        ],
      },
    ],
  },
];
